/**
 * V2ConditionRowEditor
 *
 * Single condition row in the V2 condition editor.
 * Renders: [AND/OR chip] [Variable ▼] [Operator ▼] [Value input] [✕]
 *
 * The AND/OR chip appears on all rows except the first globally.
 * It represents the connector FROM the previous row TO this row:
 *   - 'and' = same group (chip is blue/primary)
 *   - 'or'  = different group (chip is orange/warning)
 * Clicking the chip calls onLogicToggle to split/merge groups.
 *
 * Value input type adapts to variable type and operator:
 *   - select / multi-select → StyledSelectDropdown
 *   - text / number / date  → StyledTextField
 *   - none (exists/notExists) → hidden
 *
 * @module reactFlow
 */

import React, { memo, useCallback, useMemo } from 'react';
import { Box, Chip, IconButton, useTheme } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { StyledSelectDropdown, StyledTextField } from '@modules/ui-components';
import type { V2DataSource, V2ConditionRow, V2TargetValueOptions } from '@modules/v2-condition-editor';
import type { V2OperatorInfo } from '@modules/v2-variable-utils';
import type { ComparisonOperator } from '@segmently/schema-models/v2';
import type { RowConnectorLogic } from '@modules/v2-condition-editor/hooks/useV2ConditionEditor';

// ============================================================================
// TYPES
// ============================================================================

export interface V2ConditionRowEditorProps {
  row: V2ConditionRow;
  rowIndex: number; // global row index (0 = no chip; >0 = show chip)
  connectorBefore: RowConnectorLogic; // 'and', 'or', or null (no chip)
  dataSources: V2DataSource[];
  operators: V2OperatorInfo[];
  valueOptions: V2TargetValueOptions;
  isConflict?: boolean;

  onVariableChange: (variableId: string) => void;
  onOperatorChange: (operator: ComparisonOperator) => void;
  onValueChange: (value: string) => void;
  onLogicToggle: () => void;
  onRemove: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const V2ConditionRowEditor: React.FC<V2ConditionRowEditorProps> = memo(({
  row,
  rowIndex,
  connectorBefore,
  dataSources,
  operators,
  valueOptions,
  isConflict = false,
  onVariableChange,
  onOperatorChange,
  onValueChange,
  onLogicToggle,
  onRemove,
}) => {
  const theme = useTheme();

  // Build dropdown options
  const variableOptions = useMemo(() =>
    dataSources.map((ds) => ({
      value: ds.variableId,
      label: ds.variableName,
      description: `${ds.variableType} · ${ds.boundByStepName ?? 'Global'}`,
    })),
    [dataSources],
  );

  const operatorOptions = useMemo(() =>
    operators.map((op) => ({
      value: op.operator,
      label: op.label,
      description: op.description,
    })),
    [operators],
  );

  // Build value select options (for select/multi-select)
  const valueSelectOptions = useMemo(() =>
    (valueOptions.options ?? []).map((o) => ({ value: o.value, label: o.label, description: o.description })),
    [valueOptions],
  );

  const isMultiSelect = valueOptions.inputType === 'multi-select';

  // Multi-select: value stored as comma-separated string, dropdown expects array
  const multiSelectValue = useMemo(
    () => isMultiSelect && row.value ? row.value.split(',').filter(Boolean) : [],
    [isMultiSelect, row.value],
  );

  const handleSelectChange = useCallback(
    (v: string | number | (string | number)[]) => {
      if (Array.isArray(v)) {
        onValueChange(v.map(String).join(','));
      } else {
        onValueChange(String(v));
      }
    },
    [onValueChange],
  );

  // Determine row background based on validation state
  const rowBg = isConflict
    ? theme.palette.error.dark + '22'
    : !row.isValid && row.key && row.operator
      ? theme.palette.warning.dark + '11'
      : 'transparent';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {/* AND/OR chip (between rows) */}
      {connectorBefore !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
          <Chip
            label={connectorBefore === 'or' ? 'OR' : 'AND'}
            size="small"
            onClick={onLogicToggle}
            color={connectorBefore === 'or' ? 'warning' : 'primary'}
            variant="outlined"
            data-testid={`condition-logic-${rowIndex}`}
            sx={{ cursor: 'pointer', fontSize: 10, height: 20, fontWeight: 600 }}
          />
        </Box>
      )}

      {/* Row content */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1,
          borderRadius: 1,
          bgcolor: rowBg,
          border: isConflict ? `1px solid ${theme.palette.error.main}` : 'none',
        }}
      >
        {/* "Where" label for first row */}
        {rowIndex === 0 && (
          <Box sx={{
            minWidth: 44,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pr: 0.5,
            color: 'text.secondary',
            fontSize: 12,
            fontWeight: 500,
          }}>
            where
          </Box>
        )}
        {rowIndex > 0 && <Box sx={{ minWidth: 44 }} />}

        {/* Variable selector */}
        <Box sx={{ flex: '0 0 180px' }}>
          <StyledSelectDropdown
            label="Variable"
            value={row.key}
            onChange={(v) => onVariableChange(String(v))}
            options={variableOptions}
            size="small"
          />
        </Box>

        {/* Operator selector */}
        <Box sx={{ flex: '0 0 140px' }}>
          <StyledSelectDropdown
            label="Operator"
            value={row.operator}
            onChange={(v) => onOperatorChange(v as ComparisonOperator)}
            options={operatorOptions}
            disabled={!row.key}
            size="small"
          />
        </Box>

        {/* Value input */}
        {valueOptions.inputType !== 'none' && (
          <Box sx={{ flex: 1, minWidth: 100 }}>
            {(valueOptions.inputType === 'select' || isMultiSelect) ? (
              <StyledSelectDropdown
                label="Value"
                value={isMultiSelect ? multiSelectValue : row.value}
                onChange={handleSelectChange}
                options={valueSelectOptions}
                disabled={!row.operator}
                size="small"
                multiple={isMultiSelect}
              />
            ) : (
              <StyledTextField
                label="Value"
                value={row.value}
                onChange={onValueChange}
                disabled={!row.operator}
                size="small"
                type={valueOptions.inputType === 'number' ? 'number' : valueOptions.inputType === 'date' ? 'date' : 'text'}
                placeholder={valueOptions.placeholder}
                error={Boolean(row.validationError)}
                helperText={row.validationError}
                inputProps={{
                  min: valueOptions.min,
                  max: valueOptions.max,
                }}
              />
            )}
          </Box>
        )}
        {valueOptions.inputType === 'none' && <Box sx={{ flex: 1 }} />}

        {/* Remove button */}
        <IconButton size="small" onClick={onRemove} aria-label="Delete condition" sx={{ mt: 0.25 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
});

V2ConditionRowEditor.displayName = 'V2ConditionRowEditor';
